import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { SiteAdminTab } from '@lms/shared-types';
import { Tabs, TabsList, TabsTrigger, TabsPanel } from '@lms/ui';
import { filterVisibleTabs } from '@/config/siteAdminNavigation';
import { SiteAdminSectionCard } from '@/components/hub/HubComponents';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { normalizeSearch } from '@/lib/search-normalize';

export function AdministrationPage() {
  const { user, hasPermission, adminTabOptions } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState('');

  const fallbackTabs = useMemo(
    () => filterVisibleTabs(hasPermission, adminTabOptions),
    [hasPermission, adminTabOptions],
  );

  const { data: hubData } = useQuery({
    queryKey: ['admin-hub', user?.id, user?.permissions?.join(',')],
    queryFn: () => api<{ tabs: Record<string, SiteAdminTab> }>('/admin/hub'),
  });

  const tabs = hubData?.tabs ?? fallbackTabs;
  const tabKeys = Object.keys(tabs);
  const requestedTab = searchParams.get('tab');
  const activeTab =
    requestedTab && tabKeys.includes(requestedTab) ? requestedTab : (tabKeys[0] ?? 'general');

  const setActiveTab = (tab: string) => {
    setSearchParams({ tab });
  };

  useEffect(() => {
    if (requestedTab && tabKeys.length > 0 && !tabKeys.includes(requestedTab)) {
      setSearchParams({ tab: tabKeys[0]! }, { replace: true });
    }
  }, [requestedTab, tabKeys, setSearchParams]);

  const searchTerm = normalizeSearch(query.trim());
  const isSearching = searchTerm.length > 0;

  const searchResults = useMemo(() => {
    if (!isSearching) return [];
    const results: { key: string; label: string | null; links: { label: string; to: string }[] }[] = [];

    for (const [tabKey, tab] of Object.entries(tabs)) {
      tab.sections.forEach((section, sectionIndex) => {
        const labelMatches = normalizeSearch(section.label ?? '').includes(searchTerm);
        const matchingLinks = section.links.filter((link) =>
          normalizeSearch(link.label).includes(searchTerm),
        );
        if (!labelMatches && matchingLinks.length === 0) return;
        results.push({
          key: `${tabKey}-${sectionIndex}`,
          label: section.label,
          links: matchingLinks.length > 0 ? matchingLinks : section.links,
        });
      });
    }
    return results;
  }, [isSearching, searchTerm, tabs]);

  if (tabKeys.length === 0) {
    return (
      <p className="lms-site-admin-hub__denied">
        No tienes acceso a la administración del sitio.
      </p>
    );
  }

  return (
    <div className="lms-site-admin-hub">
      <header className="lms-site-admin-hub__header">
        <div className="lms-site-admin-hub__header-intro">
          <h1 className="lms-site-admin-hub__title">Administración del sitio</h1>
          <p className="lms-site-admin-hub__subtitle">
            Gestiona la configuración global del sistema, usuarios, cursos e integraciones desde este panel centralizado.
          </p>
        </div>
        <div className="lms-site-admin-hub__search-wrap">
          <label className="lms-site-admin-hub__search">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar ajustes..."
              className="lms-site-admin-hub__search-input"
            />
            <span className="material-symbols-outlined lms-site-admin-hub__search-icon" aria-hidden="true">
              search
            </span>
          </label>
        </div>
      </header>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <div className="lms-site-admin-hub__tabs">
          <TabsList>
            {tabKeys.map((key) => (
              <TabsTrigger key={key} value={key}>
                {tabs[key].label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {!isSearching &&
          tabKeys.map((key) => (
            <TabsPanel key={key} value={key}>
              <div className="lms-site-admin-hub__panel">
                {tabs[key].sections.map((section) => (
                  <SiteAdminSectionCard
                    key={`${key}-${section.label}`}
                    label={section.label}
                    links={section.links}
                  />
                ))}
              </div>
            </TabsPanel>
          ))}
      </Tabs>

      {isSearching && (
        <div className="lms-site-admin-hub__results">
          {searchResults.map((section) => (
            <SiteAdminSectionCard key={section.key} label={section.label} links={section.links} />
          ))}
          {searchResults.length === 0 && (
            <p className="lms-site-admin-hub__empty">
              No se encontraron resultados para tu búsqueda.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
